import React, { useState, useMemo } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  CATEGORIES,
  getFocusAreas,
  getIndicators,
  INDICATOR_TYPES,
  TYPE_COLORS
} from '../../data/indicatorLibrary';
import {
  ChevronRight,
  Check,
  Plus,
  X,
  Target,
  Lightbulb,
  ArrowLeft,
  Pencil
} from 'lucide-react';

export default function IndicatorSelector({ selectedIndicators, onIndicatorsChange }) {
  const [step, setStep] = useState(0); // 0=category, 1=focus, 2=indicators
  const [category, setCategory] = useState('');
  const [focusArea, setFocusArea] = useState('');
  const [showCustom, setShowCustom] = useState(false);
  const [customIndicator, setCustomIndicator] = useState({ name: '', target_value: '', description: '', indicator_type: 'Output' });
  const [editIdx, setEditIdx] = useState(null);
  const [editTarget, setEditTarget] = useState('');

  const focusAreas = useMemo(() => getFocusAreas(category), [category]);
  const indicatorGroups = useMemo(() => getIndicators(category, focusArea), [category, focusArea]);

  const isSelected = (name) => selectedIndicators.some(i => i.name === name);

  const toggleIndicator = (ind, type) => {
    if (isSelected(ind.name)) {
      onIndicatorsChange(selectedIndicators.filter(i => i.name !== ind.name));
    } else {
      onIndicatorsChange([...selectedIndicators, {
        name: ind.name,
        description: ind.description || '',
        target_value: '',
        category,
        focus_area: focusArea,
        indicator_type: type,
      }]);
    }
  };

  const updateTarget = (idx, val) => {
    const updated = [...selectedIndicators];
    updated[idx] = { ...updated[idx], target_value: val };
    onIndicatorsChange(updated);
  };

  const updateName = (idx, val) => {
    const updated = [...selectedIndicators];
    updated[idx] = { ...updated[idx], name: val };
    onIndicatorsChange(updated);
  };

  const removeIndicator = (idx) => {
    onIndicatorsChange(selectedIndicators.filter((_, i) => i !== idx));
  };

  const addCustom = () => {
    if (!customIndicator.name) return;
    onIndicatorsChange([...selectedIndicators, {
      name: customIndicator.name,
      description: customIndicator.description,
      target_value: customIndicator.target_value,
      category: category || 'Custom',
      focus_area: focusArea || 'Custom',
      indicator_type: customIndicator.indicator_type,
    }]);
    setCustomIndicator({ name: '', target_value: '', description: '', indicator_type: 'Output' });
    setShowCustom(false);
  };

  // Step 0: Category selection
  if (step === 0) {
    return (
      <div className="space-y-4" data-testid="indicator-step-category">
        <div className="flex items-center gap-2 mb-1">
          <Target className="w-4 h-4 text-[#002FA7]" />
          <Label className="text-sm font-medium">Step 1: Select Project Category</Label>
        </div>
        <div className="grid grid-cols-1 gap-2">
          {CATEGORIES.map(cat => (
            <button
              key={cat}
              type="button"
              onClick={() => { setCategory(cat); setStep(1); }}
              className="flex items-center justify-between p-4 rounded-xl border border-slate-200 hover:border-[#002FA7] hover:bg-blue-50/30 transition-all text-left group"
              data-testid={`category-${cat.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <span className="font-medium text-slate-900">{cat}</span>
              <ChevronRight className="w-4 h-4 text-slate-400 group-hover:text-[#002FA7]" />
            </button>
          ))}
        </div>
        <button
          type="button"
          onClick={() => { setShowCustom(true); setStep(3); }}
          className="text-sm text-slate-500 hover:text-[#002FA7] transition-colors mt-2 flex items-center gap-1"
          data-testid="skip-to-custom-btn"
        >
          <Plus className="w-3.5 h-3.5" /> Skip and add custom indicators
        </button>
        {selectedIndicators.length > 0 && <SelectedSummary indicators={selectedIndicators} onRemove={removeIndicator} onUpdateTarget={updateTarget} editIdx={editIdx} setEditIdx={setEditIdx} editTarget={editTarget} setEditTarget={setEditTarget} onUpdateName={updateName} />}
      </div>
    );
  }

  // Step 1: Focus area selection
  if (step === 1) {
    return (
      <div className="space-y-4" data-testid="indicator-step-focus">
        <div className="flex items-center gap-2 mb-1">
          <button type="button" onClick={() => { setStep(0); setCategory(''); }} className="text-slate-400 hover:text-slate-700">
            <ArrowLeft className="w-4 h-4" />
          </button>
          <Target className="w-4 h-4 text-[#002FA7]" />
          <Label className="text-sm font-medium">Step 2: Select Focus Area</Label>
          <Badge variant="secondary" className="text-xs">{category}</Badge>
        </div>
        <div className="grid grid-cols-1 gap-2">
          {focusAreas.map(fa => (
            <button
              key={fa}
              type="button"
              onClick={() => { setFocusArea(fa); setStep(2); }}
              className="flex items-center justify-between p-4 rounded-xl border border-slate-200 hover:border-[#002FA7] hover:bg-blue-50/30 transition-all text-left group"
              data-testid={`focus-${fa.toLowerCase().replace(/[/ ]/g, '-')}`}
            >
              <span className="font-medium text-slate-900">{fa}</span>
              <ChevronRight className="w-4 h-4 text-slate-400 group-hover:text-[#002FA7]" />
            </button>
          ))}
        </div>
        {selectedIndicators.length > 0 && <SelectedSummary indicators={selectedIndicators} onRemove={removeIndicator} onUpdateTarget={updateTarget} editIdx={editIdx} setEditIdx={setEditIdx} editTarget={editTarget} setEditTarget={setEditTarget} onUpdateName={updateName} />}
      </div>
    );
  }

  // Step 2: Indicator selection
  if (step === 2) {
    return (
      <div className="space-y-5" data-testid="indicator-step-select">
        <div className="flex items-center gap-2 mb-1">
          <button type="button" onClick={() => { setStep(1); setFocusArea(''); }} className="text-slate-400 hover:text-slate-700">
            <ArrowLeft className="w-4 h-4" />
          </button>
          <Target className="w-4 h-4 text-[#002FA7]" />
          <Label className="text-sm font-medium">Step 3: Select Indicators</Label>
          <Badge variant="secondary" className="text-xs">{category}</Badge>
          <ChevronRight className="w-3 h-3 text-slate-300" />
          <Badge variant="secondary" className="text-xs">{focusArea}</Badge>
        </div>

        <div className="flex items-start gap-2 p-3 bg-blue-50/60 rounded-lg border border-blue-100">
          <Lightbulb className="w-4 h-4 text-[#002FA7] mt-0.5 flex-shrink-0" />
          <p className="text-xs text-slate-600">Select the indicators relevant to your project. You can set target values and edit names after selecting. All indicators are optional.</p>
        </div>

        {INDICATOR_TYPES.filter(type => indicatorGroups[type]?.length > 0).map(type => {
          const colors = TYPE_COLORS[type];
          return (
            <div key={type}>
              <div className="flex items-center gap-2 mb-2">
                <span className={`text-xs font-bold uppercase tracking-wider ${colors.text}`}>{type} Indicators</span>
                <div className={`flex-1 h-px ${colors.border} border-t`} />
              </div>
              <div className="space-y-1.5">
                {indicatorGroups[type].map(ind => {
                  const selected = isSelected(ind.name);
                  return (
                    <button
                      key={ind.name}
                      type="button"
                      onClick={() => toggleIndicator(ind, type)}
                      className={`w-full flex items-start gap-3 p-3 rounded-lg border transition-all text-left ${
                        selected ? `${colors.bg} ${colors.border} border` : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                      }`}
                      data-testid={`indicator-option-${ind.name.toLowerCase().replace(/\s+/g, '-').substring(0, 30)}`}
                    >
                      <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 mt-0.5 transition-colors ${
                        selected ? 'bg-[#002FA7] border-[#002FA7]' : 'border-slate-300'
                      }`}>
                        {selected && <Check className="w-3 h-3 text-white" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm font-medium ${selected ? 'text-slate-900' : 'text-slate-700'}`}>{ind.name}</p>
                        {ind.description && <p className="text-xs text-slate-500 mt-0.5">{ind.description}</p>}
                      </div>
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full flex-shrink-0 ${colors.badge}`}>{type}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}

        {/* Add custom */}
        <div className="pt-2 border-t border-slate-200">
          {!showCustom ? (
            <button type="button" onClick={() => setShowCustom(true)} className="text-sm text-[#002FA7] hover:underline flex items-center gap-1" data-testid="add-custom-indicator-btn">
              <Plus className="w-3.5 h-3.5" /> Add a custom indicator
            </button>
          ) : (
            <CustomIndicatorForm customIndicator={customIndicator} setCustomIndicator={setCustomIndicator} onAdd={addCustom} onCancel={() => setShowCustom(false)} />
          )}
        </div>

        <SelectedSummary indicators={selectedIndicators} onRemove={removeIndicator} onUpdateTarget={updateTarget} editIdx={editIdx} setEditIdx={setEditIdx} editTarget={editTarget} setEditTarget={setEditTarget} onUpdateName={updateName} />

        <button type="button" onClick={() => { setStep(1); setFocusArea(''); }} className="text-sm text-slate-500 hover:text-slate-700 flex items-center gap-1">
          <ArrowLeft className="w-3.5 h-3.5" /> Back to focus areas
        </button>
      </div>
    );
  }

  // Step 3: Custom only
  if (step === 3) {
    return (
      <div className="space-y-4" data-testid="indicator-step-custom">
        <div className="flex items-center gap-2 mb-1">
          <button type="button" onClick={() => { setStep(0); setShowCustom(false); }} className="text-slate-400 hover:text-slate-700">
            <ArrowLeft className="w-4 h-4" />
          </button>
          <Target className="w-4 h-4 text-[#002FA7]" />
          <Label className="text-sm font-medium">Custom Indicators</Label>
        </div>
        <CustomIndicatorForm customIndicator={customIndicator} setCustomIndicator={setCustomIndicator} onAdd={addCustom} onCancel={() => { setStep(0); setShowCustom(false); }} />
        <SelectedSummary indicators={selectedIndicators} onRemove={removeIndicator} onUpdateTarget={updateTarget} editIdx={editIdx} setEditIdx={setEditIdx} editTarget={editTarget} setEditTarget={setEditTarget} onUpdateName={updateName} />
      </div>
    );
  }

  return null;
}

function CustomIndicatorForm({ customIndicator, setCustomIndicator, onAdd, onCancel }) {
  return (
    <div className="space-y-2 p-4 bg-slate-50 rounded-xl border border-dashed border-slate-300" data-testid="custom-indicator-form">
      <div className="flex gap-2">
        <Input
          placeholder="Indicator name"
          value={customIndicator.name}
          onChange={(e) => setCustomIndicator({ ...customIndicator, name: e.target.value })}
          data-testid="custom-indicator-name"
        />
        <Select value={customIndicator.indicator_type} onValueChange={(v) => setCustomIndicator({ ...customIndicator, indicator_type: v })}>
          <SelectTrigger className="w-32">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="Input">Input</SelectItem>
            <SelectItem value="Output">Output</SelectItem>
            <SelectItem value="Outcome">Outcome</SelectItem>
            <SelectItem value="Impact">Impact</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="flex gap-2">
        <Input
          placeholder="Target value"
          value={customIndicator.target_value}
          onChange={(e) => setCustomIndicator({ ...customIndicator, target_value: e.target.value })}
          data-testid="custom-indicator-target"
        />
        <Input
          placeholder="Description (optional)"
          value={customIndicator.description}
          onChange={(e) => setCustomIndicator({ ...customIndicator, description: e.target.value })}
        />
      </div>
      <div className="flex gap-2 justify-end">
        <Button type="button" variant="ghost" size="sm" onClick={onCancel}>Cancel</Button>
        <Button type="button" size="sm" onClick={onAdd} disabled={!customIndicator.name} data-testid="confirm-custom-indicator-btn">
          <Plus className="w-3.5 h-3.5 mr-1" /> Add Indicator
        </Button>
      </div>
    </div>
  );
}

function SelectedSummary({ indicators, onRemove, onUpdateTarget, editIdx, setEditIdx, editTarget, setEditTarget, onUpdateName }) {
  if (indicators.length === 0) return null;

  return (
    <div className="space-y-2 pt-3 border-t border-slate-200" data-testid="selected-indicators-summary">
      <Label className="text-sm font-medium text-slate-900">Selected Indicators ({indicators.length})</Label>
      <div className="space-y-1.5">
        {indicators.map((ind, idx) => {
          const colors = TYPE_COLORS[ind.indicator_type] || TYPE_COLORS.Output;
          const isEditing = editIdx === idx;
          return (
            <div key={idx} className={`flex items-center gap-2 p-2.5 rounded-lg border ${colors.border} ${colors.bg}`} data-testid={`selected-indicator-${idx}`}>
              <div className="flex-1 min-w-0">
                {isEditing ? (
                  <Input
                    className="h-7 text-sm"
                    value={ind.name}
                    onChange={(e) => onUpdateName(idx, e.target.value)}
                    onBlur={() => setEditIdx(null)}
                    autoFocus
                  />
                ) : (
                  <div className="flex items-center gap-1.5">
                    <p className="text-sm font-medium text-slate-900 truncate">{ind.name}</p>
                    <button type="button" onClick={() => setEditIdx(idx)} className="text-slate-400 hover:text-slate-600">
                      <Pencil className="w-3 h-3" />
                    </button>
                  </div>
                )}
              </div>
              <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full flex-shrink-0 ${colors.badge}`}>{ind.indicator_type || 'Output'}</span>
              <Input
                className="w-24 h-7 text-sm text-center"
                placeholder="Target"
                value={ind.target_value}
                onChange={(e) => onUpdateTarget(idx, e.target.value)}
                data-testid={`selected-indicator-target-${idx}`}
              />
              <button type="button" onClick={() => onRemove(idx)} className="text-slate-400 hover:text-red-500 transition-colors flex-shrink-0">
                <X className="w-4 h-4" />
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
